import zod from 'zod';

/**
 * The stored shape of a date-ish cell.
 *
 * `date` columns have always accepted a bare timestamp (the original shape) and
 * later gained the `{ start, end?, hasTime? }` object. `date-range` columns
 * reuse the same union so that switching a column between the two types — or
 * reading a document written by an older build — never fails
 * `rawValue.schema.safeParse`, which would make `cellValueGet` drop the value.
 */
export const dateObjectSchema = zod.object({
  start: zod.number(),
  end: zod.number().optional(),
  hasTime: zod.boolean().optional(),
});

export const dateValueSchema = zod.union([
  zod.number(),
  dateObjectSchema,
  zod.null(),
]);

/** The json value of a `date-range` column: always an object, or null. */
export const dateRangeJsonSchema = zod.union([dateObjectSchema, zod.null()]);

export type NormalizedDate = { start: number; end?: number; hasTime: boolean };

/** Like {@link NormalizedDate}, but `end` is filled in and never before `start`. */
export type NormalizedDateRange = {
  start: number;
  end: number;
  hasTime: boolean;
};

export function normalizeDate(value: unknown): NormalizedDate | null {
  if (value == null) return null;
  if (typeof value === 'number') {
    return { start: value, hasTime: false };
  }
  if (typeof value === 'object' && value !== null && 'start' in value) {
    const obj = value as { start: number; end?: number; hasTime?: boolean };
    return { start: obj.start, end: obj.end, hasTime: !!obj.hasTime };
  }
  return null;
}

/**
 * Same as {@link normalizeDate}, but collapses the "no end yet" and "end before
 * start" cases so callers never have to branch on them.
 */
export function normalizeDateRange(value: unknown): NormalizedDateRange | null {
  const date = normalizeDate(value);
  if (!date) return null;
  const end = date.end ?? date.start;
  return end < date.start
    ? { start: end, end: date.start, hasTime: date.hasTime }
    : { start: date.start, end, hasTime: date.hasTime };
}

/**
 * The single timestamp a date-ish value sorts, groups and filters by.
 *
 * Every `t.date` consumer runs values through this, because `date-range`
 * columns are declared assignable to `t.date` but carry an object.
 */
export function toDateTimestamp(value: unknown): number | null {
  const start = normalizeDate(value)?.start;
  return typeof start === 'number' && Number.isFinite(start) ? start : null;
}

/** The end of a date-ish value, falling back to its start. */
export function toDateEndTimestamp(value: unknown): number | null {
  const date = normalizeDateRange(value);
  return date && Number.isFinite(date.end) ? date.end : null;
}

/**
 * Move a value so it starts at `startTs`, keeping its duration, its shape
 * (bare timestamp vs object) and `hasTime`.
 */
export function shiftDateValueTo(value: unknown, startTs: number): unknown {
  const date = normalizeDate(value);
  if (!date) return startTs;
  if (date.end == null) {
    return typeof value === 'number'
      ? startTs
      : { start: startTs, hasTime: date.hasTime };
  }
  return {
    start: startTs,
    end: startTs + (date.end - date.start),
    hasTime: date.hasTime,
  };
}
